/**
 * Per-page copy for the Enterprise AI Upskilling pages.
 *
 * Each entry is authored for its own domain. These pages were previously
 * clones of the finance page with nouns swapped, which left finance language
 * (GL data, trial balance, AR aging, month-end close) on HR, manufacturing and
 * general-enterprise pages. Nothing here is shared boilerplate.
 *
 * Testimonials are deliberately absent: the quotes previously on these pages
 * were attributed to invented individuals. Real, NDA-anonymised quotes can be
 * added per page once available.
 */

/* ===================================================================== */
/* Copilot for HR                                                        */
/* ===================================================================== */

const hr = {
  page: '/copilot-hr-training',
  product: 'Microsoft Copilot',
  roi: { productName: 'Copilot', defaultSeatCost: 360 },

  hero: {
    eyebrow: '1-Day HR Training • Delivered Onsite at Your Office',
    image: '/images/hr-training.png',
    titleLead: 'Your HR team has Copilot. They are still ',
    titleAccent: 'copy-pasting between systems',
    titleTail: '.',
    sub: 'We train your HR team onsite, on your own policies, job descriptions and case notes, to build Copilot workflows for screening, onboarding and employee queries — in one day.',
    ctaLabel: 'Book Your 1-Day HR Training →',
    gtag: 'Hero Book HR Training',
    security: 'Runs inside your Microsoft 365 tenant, under your existing access controls. Enterprise NDA.',
    badges: [
      { color: '#34d399', title: 'Private 1 Day', sub: 'Intensive' },
      { color: '#38bdf8', title: '70% Hands-On', sub: 'Active Learning' },
      { color: '#c084fc', title: 'HR Built', sub: 'Talent Workflows' },
      { color: '#fbbf24', title: 'Your Data', sub: 'Policies & Case Notes' }
    ]
  },

  comparison: {
    heading: 'This is not a Copilot demo. It is a working session for HR professionals.',
    lead: 'Your HR team does not need to be shown how to summarise a meeting. They need the recruitment pipeline, the onboarding pack and the policy inbox to take less time next Monday than they did last Monday.',
    genericTitle: 'Generic Copilot Training',
    generic: [
      { strong: 'Wrong problems', text: 'Teaches meeting recaps and email drafting — work your HR team was never the bottleneck on.' },
      { strong: 'Wrong teacher', text: 'Delivered by IT generalists who have never run a hiring loop or handled a grievance case.' },
      { strong: 'Wrong data', text: 'Uses sample datasets, so nothing learned survives contact with your actual applicant records and policy library.' },
      { strong: 'Wrong depth', text: 'Stops at chat prompts, never reaching document generation, comparison or structured extraction.' }
    ],
    specialisedTitle: 'Specialised HR Training',
    specialised: [
      { strong: 'Real HR workflows', text: 'CV screening against your own criteria, offer and contract drafting, onboarding packs, policy Q&A.' },
      { strong: 'Practitioner led', text: 'Taught by someone who has delivered HR and workforce systems inside large regulated organisations.' },
      { strong: 'Your material', text: 'Your job descriptions, your handbook, your competency framework — with anonymised records where sensitivity requires it.' },
      { strong: 'Beyond prompting', text: 'Reusable prompt libraries and at least one working HR use case built during the session.' }
    ]
  },

  learnDo: {
    heading: 'Six HR workflows, built by your team, during the day.',
    subheading: 'Roughly 30% instruction, 70% hands-on. Everyone works on their own machine, in your tenant, on work they actually own.',
    pills: ['CV Screening', 'Job Descriptions', 'Onboarding Packs', 'Policy Q&A', 'Interview Guides', 'HR Reporting']
  },

  beforeAfter: {
    heading: 'What changes the next morning',
    subheading: 'Not capability in the abstract. Specific tasks that took a morning and now take a coffee break.',
    rows: [
      { before: 'Screening 120 CVs by hand against a role spec', after: 'Structured shortlist with reasoning against your own criteria, reviewed rather than compiled' },
      { before: 'Writing every job description from the last similar one', after: 'Drafted from your competency framework and tone of voice, then edited' },
      { before: 'Onboarding packs assembled from six folders per hire', after: 'Generated per role and location from your template library' },
      { before: 'Policy questions answered by whoever remembers the clause', after: 'Answered against your handbook, with the clause cited for verification' },
      { before: 'Headcount and attrition reporting rebuilt manually each cycle', after: 'Assembled from your export in minutes, with commentary drafted for review' }
    ]
  },

  agenda: {
    heading: 'One day. Your office. Hour by hour.',
    subheading: 'No slideware marathon. Short teaching blocks, then your team works.',
    items: [
      { time: '9:00', title: 'Foundations that actually matter for HR', body: 'What Copilot can and cannot do with employee data, where your tenant boundary sits, and the failure modes to expect. Short, practical, no hype.' },
      { time: '10:00', title: 'Screening and shortlisting', body: 'Build a repeatable screening prompt against your own role criteria. Handle bias language, edge cases, and the point at which a human must decide.' },
      { time: '11:30', title: 'Job descriptions and offer documents', body: 'Draft from your competency framework and house style. Version, compare, and standardise so output is consistent across recruiters.' },
      { time: '1:30', title: 'Onboarding and policy Q&A', body: 'Generate role-specific onboarding packs. Build a grounded Q&A pattern over your handbook that cites the clause rather than paraphrasing it.' },
      { time: '3:00', title: 'HR reporting and executive summaries', body: 'Turn a headcount or attrition export into a clean summary with commentary your HR director can send on after review.' },
      { time: '4:00', title: 'Beyond prompting: build an HR use case', body: 'Each group builds one working HR use case end to end — usually screening, onboarding or policy Q&A — and demonstrates it to the room.' },
      { time: '5:00', title: 'Take it back to work', body: 'Prompt library exported, owners assigned, and an agreed list of what the team will use next week.' }
    ]
  },

  deliverables: {
    heading: 'What your HR team leaves with',
    subheading: 'Artefacts, not a certificate.',
    items: [
      { title: 'A working HR use case', body: 'Built during the session on your own material, demonstrated to the room, and ready to use on Monday.' },
      { title: 'A reusable prompt library', body: 'Every prompt developed on the day, documented and exported, so a new joiner can pick it up without retraining.' },
      { title: 'A screening pattern for your roles', body: 'Structured against your own criteria, with the human decision point made explicit rather than assumed.' },
      { title: 'Grounded policy Q&A pattern', body: 'A method for answering handbook questions with the source clause attached, so answers can be checked.' },
      { title: 'A named owner per workflow', body: 'Adoption dies when nothing is owned. Each workflow leaves with a person against it.' },
      { title: 'A short adoption plan', body: 'What the team will use in week one, what needs IT involvement, and what is deliberately parked.' }
    ]
  },

  qualification: {
    heading: 'Who this training is for.',
    subheading: 'A poor fit wastes a full day on both sides, so we would rather establish that on the call than on the day.',
    isFor: [
      'HR teams that already hold Microsoft 365 Copilot licences',
      'Six or more HR people who can be released for a full day',
      'Willingness to work on your own documents, anonymised where needed',
      'An HR leader who can decide what the team adopts afterwards'
    ],
    isNotFor: [
      'No Copilot licences yet — talk to us about readiness first instead',
      'Looking for an awareness webinar rather than hands-on work',
      'Unable to release the team for a full uninterrupted day',
      'Expecting a system integration; this is training, not implementation'
    ]
  },

  guarantee: {
    heading: 'What your team will have built by 5pm.',
    body: 'The session is built around one commitment: your team applies Copilot to real HR workflows and leaves with at least one working HR use case they built themselves, on your own material. We scope the day with you beforehand so that outcome is realistic for your team.'
  },

  trainer: {
    heading: 'Not taught by an AI generalist',
    intro: 'Your team will be able to tell within twenty minutes whether the person at the front has ever run this kind of work. This is who runs the session.'
  },

  confidentiality: 'We deliver these sessions under enterprise NDA, and client names are withheld — the same confidentiality that will cover your own session. If you would like a reference conversation, we will arrange one directly with a past client rather than publish a quote.',

  faqs: [
    {
      question: 'Is our employee data safe during onsite training?',
      answer: 'We sign enterprise NDAs prior to the session, and all hands-on exercises use your organisation\'s approved Microsoft 365 tools under your existing tenant permissions and access controls. Where records are particularly sensitive we work from anonymised extracts your team prepares. Microsoft states that Copilot prompts, responses, and Microsoft Graph data are not used to train its foundation models.'
    },
    {
      question: 'What exactly will our team have at the end of the day?',
      answer: 'By the end of the workshop your team will have applied Copilot to real HR workflows and built at least one working HR use case during the session. We agree the specific use case with you before the session so it is realistic for your team and their data.'
    },
    {
      question: 'How is this different from our IT department\'s Copilot rollout session?',
      answer: 'An IT rollout session teaches the tool. This teaches the work. We spend the day inside HR workflows — screening, onboarding, policy questions, reporting — using your own material, with an outcome the team demonstrates before they leave.'
    },
    {
      question: 'Can Copilot be used to make hiring decisions?',
      answer: 'No, and we teach it that way. Copilot can structure, summarise and compare candidate information to make review faster, but the assessment decision stays with your recruiters. We make the human decision point explicit in every screening workflow we build, which is also what most employment regulators expect.'
    },
    {
      question: 'How many people can attend?',
      answer: 'Up to around 15 works well for hands-on coaching. Beyond that, we split into two sessions rather than dilute the room. Pricing is a flat day rate for the team, not per seat.'
    },
    {
      question: 'Do you deliver outside Singapore?',
      answer: 'Yes. We deliver onsite across Singapore and the wider Southeast Asia region, and travel further for larger engagements. Remote delivery is possible but hands-on coaching is noticeably better in the room.'
    }
  ],

  finalCta: {
    eyebrow: 'By application',
    heading: 'You have already paid for the licences.',
    subheading: 'Now invest one day in teaching HR how to use them.',
    note: 'One flat day rate for your whole team — no per-seat pricing. Weigh it against the underused licence spend in the calculator above.',
    ps: 'All hands-on exercises run privately inside your organisation\'s Microsoft 365 environment, under your existing tenant permissions and an enterprise NDA.'
  },

  leadForm: {
    title: 'Request Onsite HR Training',
    subtitle: 'We come to your office. Private session, your own HR material.',
    buttonLabel: 'Request Onsite Training',
    messageLabel: 'What is your biggest HR bottleneck? (optional)',
    messagePlaceholder: 'e.g. Screening takes two weeks per role, onboarding packs are assembled by hand...',
    successText: 'Thanks — we\'ll respond within 24 hours to scope a private onsite session for your HR team.'
  }
};

/* ===================================================================== */
/* Copilot for Manufacturing                                             */
/* ===================================================================== */

const manufacturing = {
  page: '/copilot-manufacturing-training',
  product: 'Microsoft Copilot',
  roi: { productName: 'Copilot', defaultSeatCost: 360 },

  hero: {
    eyebrow: '1-Day Manufacturing Training • Delivered Onsite at Your Plant',
    image: '/images/mfg-training.png',
    titleLead: 'Your plant runs on ',
    titleAccent: 'spreadsheets and shift handovers',
    titleTail: '. Copilot has not changed that yet.',
    sub: 'We train your operations, quality and supply chain teams onsite, on your own production and quality data, to build Copilot workflows for shift reporting, deviation write-ups and supplier correspondence — in one day.',
    ctaLabel: 'Book Your 1-Day Plant Training →',
    gtag: 'Hero Book Manufacturing Training',
    security: 'Runs inside your Microsoft 365 tenant, under your existing access controls. Enterprise NDA.',
    badges: [
      { color: '#34d399', title: 'Private 1 Day', sub: 'Intensive' },
      { color: '#38bdf8', title: '70% Hands-On', sub: 'Active Learning' },
      { color: '#c084fc', title: 'Plant Built', sub: 'Ops & Quality Workflows' },
      { color: '#fbbf24', title: 'Your Data', sub: 'Production & QA Records' }
    ]
  },

  comparison: {
    heading: 'This is not a Copilot demo. It is a working session for operations teams.',
    lead: 'Your supervisors do not need help writing emails. They need the shift report, the deviation write-up and the supplier chase to stop eating the end of every shift.',
    genericTitle: 'Generic Copilot Training',
    generic: [
      { strong: 'Office-worker framing', text: 'Built for desk-based knowledge work, not for people who spend most of the day on the floor.' },
      { strong: 'No process context', text: 'Delivered by trainers who have never read a deviation report or a supplier corrective action.' },
      { strong: 'Sample data', text: 'Demo datasets that look nothing like your production logs, QA records or maintenance history.' },
      { strong: 'No handover', text: 'Nobody owns anything afterwards, so the plant returns to the old spreadsheet on day two.' }
    ],
    specialisedTitle: 'Specialised Manufacturing Training',
    specialised: [
      { strong: 'Plant workflows', text: 'Shift reports, deviation and non-conformance write-ups, supplier correspondence, maintenance summaries.' },
      { strong: 'Practitioner led', text: 'Taught by someone who has delivered data and reporting systems into manufacturing environments.' },
      { strong: 'Your records', text: 'Your production exports, quality logs and templates — anonymised where commercially sensitive.' },
      { strong: 'Beyond prompting', text: 'A reusable prompt library and at least one working plant use case built during the session.' }
    ]
  },

  learnDo: {
    heading: 'Six plant workflows, built by your team, during the day.',
    subheading: 'Roughly 30% instruction, 70% hands-on, on your own operational records.',
    pills: ['Shift Reporting', 'Deviation Write-Ups', 'Supplier Correspondence', 'Maintenance Summaries', 'SOP Drafting', 'Production Analysis']
  },

  beforeAfter: {
    heading: 'What changes on the next shift',
    subheading: 'Specific end-of-shift tasks that took an hour and now take ten minutes.',
    rows: [
      { before: 'Shift report written from memory and scattered notes at the end of a twelve-hour day', after: 'Drafted from the shift log in your own format, then corrected and signed off' },
      { before: 'Deviation write-ups delayed because nobody has time to write them properly', after: 'Structured first draft against your NCR template, ready for quality review' },
      { before: 'Supplier chasers and corrective action letters written from scratch each time', after: 'Drafted from the issue record, consistent in tone and traceable' },
      { before: 'Production data pulled into Excel and read manually for the daily meeting', after: 'Summarised with the exceptions surfaced first, for a human to verify' },
      { before: 'SOP updates postponed for months because rewriting them is slow', after: 'Drafted against the current process description and routed for approval' }
    ]
  },

  agenda: {
    heading: 'One day. Your plant. Hour by hour.',
    subheading: 'Short teaching blocks, then your supervisors and engineers work on their own records.',
    items: [
      { time: '9:00', title: 'Foundations for operations teams', body: 'What Copilot does reliably with operational text and tabular data, what it does badly, and where a human must stay in the loop for quality-critical work.' },
      { time: '10:00', title: 'Shift reporting', body: 'Turn a shift log into your standard report format. Handle missing entries, ambiguity and the sections that must never be auto-generated.' },
      { time: '11:30', title: 'Deviations and non-conformance', body: 'Draft NCR and deviation write-ups against your own template, with the evidence trail preserved and the investigation left to your engineers.' },
      { time: '1:30', title: 'Supplier and maintenance correspondence', body: 'Corrective action requests, chasers and maintenance summaries drafted from the underlying record rather than rewritten each time.' },
      { time: '3:00', title: 'Production data and daily meeting prep', body: 'Take a production or downtime export and produce the summary your daily meeting actually needs, exceptions first.' },
      { time: '4:00', title: 'Beyond prompting: build a plant use case', body: 'Each group builds one working use case end to end and demonstrates it — usually shift reporting or deviation drafting.' },
      { time: '5:00', title: 'Take it back to the floor', body: 'Prompt library exported, owners assigned per shift or function, and agreement on what runs live next week.' }
    ]
  },

  deliverables: {
    heading: 'What your plant team leaves with',
    subheading: 'Working artefacts your shifts can use immediately.',
    items: [
      { title: 'A working plant use case', body: 'Built on your own records during the session and demonstrated before anyone leaves the room.' },
      { title: 'A reusable prompt library', body: 'Every prompt developed on the day, documented and exported so it transfers across shifts.' },
      { title: 'A shift report pattern', body: 'Matched to your existing format, including the sections that must remain human-written.' },
      { title: 'A deviation drafting pattern', body: 'Structured against your NCR template with the evidence trail and review step preserved.' },
      { title: 'A named owner per workflow', body: 'Assigned per shift or function so adoption survives the next rotation.' },
      { title: 'A short adoption plan', body: 'What runs live next week, what needs IT or QA sign-off, and what is deliberately parked.' }
    ]
  },

  qualification: {
    heading: 'Who this training is for.',
    subheading: 'A poor fit costs a full production day on both sides. We would rather establish that on the call than on the day.',
    isFor: [
      'Manufacturers whose operations staff already hold Microsoft 365 Copilot licences',
      'Six or more supervisors, quality or supply chain staff who can be released for a day',
      'Willingness to work on your own production and quality records',
      'An operations or quality leader who can decide what gets adopted'
    ],
    isNotFor: [
      'No Copilot licences yet — talk to us about readiness first instead',
      'Looking for an awareness session rather than hands-on work',
      'Expecting Copilot to touch MES, SCADA or PLC systems; this is document and reporting work',
      'Unable to release supervisors for an uninterrupted day'
    ]
  },

  guarantee: {
    heading: 'What your team will have built by 5pm.',
    body: 'The session is built around one commitment: your team applies Copilot to real operational workflows and leaves with at least one working plant use case they built themselves, on your own records. We scope the day with you beforehand so that outcome is realistic for your team.'
  },

  trainer: {
    heading: 'Not taught by an AI generalist',
    intro: 'Operations teams have a low tolerance for theory. This is who runs the session and what they have actually delivered.'
  },

  confidentiality: 'We deliver these sessions under enterprise NDA, and client names are withheld — the same confidentiality that will cover your own session. If you would like a reference conversation, we will arrange one directly with a past client rather than publish a quote.',

  faqs: [
    {
      question: 'Is our production and quality data safe during onsite training?',
      answer: 'We sign enterprise NDAs prior to the session, and all hands-on exercises use your organisation\'s approved Microsoft 365 tools under your existing tenant permissions and access controls. Commercially sensitive records can be anonymised by your team beforehand. Microsoft states that Copilot prompts, responses, and Microsoft Graph data are not used to train its foundation models.'
    },
    {
      question: 'Does this touch our MES, SCADA or PLC systems?',
      answer: 'No. This is training on document, reporting and correspondence workflows inside Microsoft 365. We do not connect to, configure or read from control or execution systems, and we would treat any such work as a separate engineering engagement with its own safety review.'
    },
    {
      question: 'Can Copilot write our deviation and non-conformance reports?',
      answer: 'It can produce a structured first draft from the underlying record, which is usually where the time goes. The investigation, the root cause and the sign-off stay with your quality team. We build the workflow so the human review step is explicit rather than assumed — which is what your auditors will ask about.'
    },
    {
      question: 'What exactly will our team have at the end of the day?',
      answer: 'By the end of the workshop your team will have applied Copilot to real operational workflows and built at least one working plant use case during the session. We agree the specific use case with you before the session so it is realistic for your team and their data.'
    },
    {
      question: 'Can you run it across shifts?',
      answer: 'Yes. For plants running continuous operations we usually split into two sessions on consecutive days rather than pull an entire shift off the floor at once.'
    },
    {
      question: 'Do you deliver outside Singapore?',
      answer: 'Yes. We deliver onsite across Singapore and the wider Southeast Asia region, and travel further for larger engagements.'
    }
  ],

  finalCta: {
    eyebrow: 'By application',
    heading: 'You have already paid for the licences.',
    subheading: 'Now invest one day in teaching your plant teams to use them.',
    note: 'One flat day rate for your whole team — no per-seat pricing. Weigh it against the underused licence spend in the calculator above.',
    ps: 'All hands-on exercises run privately inside your organisation\'s Microsoft 365 environment, under your existing tenant permissions and an enterprise NDA.'
  },

  leadForm: {
    title: 'Request Onsite Plant Training',
    subtitle: 'We come to your plant. Private session, your own operational records.',
    buttonLabel: 'Request Onsite Training',
    messageLabel: 'What is your biggest reporting bottleneck? (optional)',
    messagePlaceholder: 'e.g. Shift reports are written at 8pm from memory, deviation write-ups run two weeks behind...',
    successText: 'Thanks — we\'ll respond within 24 hours to scope a private onsite session for your operations team.'
  }
};

/* ===================================================================== */
/* Gemini Enterprise                                                     */
/* ===================================================================== */

const gemini = {
  page: '/gemini-enterprise-training',
  product: 'Google Gemini',
  roi: { productName: 'Gemini', defaultSeatCost: 300 },

  hero: {
    eyebrow: '1-Day Gemini Training • Delivered Onsite at Your Office',
    image: '/images/gemini-training.png',
    titleLead: 'Gemini is in your Workspace. Your team still works ',
    titleAccent: 'the old way',
    titleTail: '.',
    sub: 'We train your team onsite, inside your own Google Workspace, to build Gemini workflows in Docs, Sheets, Slides and Gmail that hold up on real work — in one day.',
    ctaLabel: 'Book Your 1-Day Gemini Training →',
    gtag: 'Hero Book Gemini Training',
    security: 'Runs inside your own Google Workspace, under your existing access controls. Enterprise NDA.',
    badges: [
      { color: '#34d399', title: 'Private 1 Day', sub: 'Intensive' },
      { color: '#38bdf8', title: '70% Hands-On', sub: 'Active Learning' },
      { color: '#c084fc', title: 'Workspace Built', sub: 'Docs, Sheets, Slides' },
      { color: '#fbbf24', title: 'Your Data', sub: 'Your Drive, Your Files' }
    ]
  },

  comparison: {
    heading: 'This is not a Gemini demo. It is a working session inside your Workspace.',
    lead: 'Everyone can already ask Gemini to summarise something. The gap is between a party trick and a workflow a team of forty runs the same way every week.',
    genericTitle: 'Generic AI Training',
    generic: [
      { strong: 'Tool tour', text: 'Walks the interface and the feature list rather than the work your team is behind on.' },
      { strong: 'Chat-only', text: 'Stays in the chat box and never reaches Gemini inside Docs, Sheets, Slides and Drive where the work lives.' },
      { strong: 'Sample files', text: 'Demo documents, so nothing survives contact with your real Drive structure and file quality.' },
      { strong: 'No standard', text: 'Everyone leaves with a personal habit and no shared way of working.' }
    ],
    specialisedTitle: 'Specialised Workspace Training',
    specialised: [
      { strong: 'Real workflows', text: 'Document drafting, Sheets analysis, deck generation, inbox triage and grounded search across your own Drive.' },
      { strong: 'Practitioner led', text: 'Taught by someone who has built enterprise data and AI systems, not a vendor demo specialist.' },
      { strong: 'Your Drive', text: 'Your own documents and sheets, under your existing Workspace permissions.' },
      { strong: 'A shared standard', text: 'A prompt library and agreed patterns the whole team uses, not forty personal styles.' }
    ]
  },

  learnDo: {
    heading: 'Six Workspace workflows, built by your team, during the day.',
    subheading: 'Roughly 30% instruction, 70% hands-on, in your own Workspace on your own files.',
    pills: ['Docs Drafting', 'Sheets Analysis', 'Slides Generation', 'Gmail Triage', 'Grounded Drive Search', 'Meeting Follow-Ups']
  },

  beforeAfter: {
    heading: 'What changes the next morning',
    subheading: 'Concrete tasks, not general capability.',
    rows: [
      { before: 'Long documents read end to end before anyone can act on them', after: 'Summarised with the decisions and open questions pulled out, then verified' },
      { before: 'Sheets analysis done by whoever is best at formulas', after: 'Described in plain language, generated, then checked against a known result' },
      { before: 'Decks built by copying the last similar deck', after: 'Drafted from the underlying document and restructured for the audience' },
      { before: 'Inbox triage consuming the first hour of every day', after: 'Sorted and drafted against your own reply patterns, reviewed before sending' },
      { before: 'Nobody can find the current version of anything in Drive', after: 'Grounded search across your Drive that cites the file it answered from' }
    ]
  },

  agenda: {
    heading: 'One day. Your office. Hour by hour.',
    subheading: 'Short teaching blocks, then your team works in your own Workspace.',
    items: [
      { time: '9:00', title: 'Foundations that matter', body: 'What Gemini reliably does with your documents and data, where it fails, and how grounding in your Drive changes the quality of every answer.' },
      { time: '10:00', title: 'Docs: drafting and reworking', body: 'Draft, restructure and tighten real documents. Build house-style patterns so output is consistent across the team rather than per person.' },
      { time: '11:30', title: 'Sheets: analysis without formula fluency', body: 'Describe the analysis in plain language, generate it, and — the part most training skips — verify it against a result you already know.' },
      { time: '1:30', title: 'Slides and communication', body: 'Turn a document into a structured deck for a specific audience, then rework it rather than accepting the first draft.' },
      { time: '3:00', title: 'Gmail and grounded Drive search', body: 'Inbox triage against your own reply patterns, and search grounded in your Drive that cites the source file.' },
      { time: '4:00', title: 'Beyond prompting: build a team use case', body: 'Each group builds one working workflow end to end on real work and demonstrates it to the room.' },
      { time: '5:00', title: 'Take it back to work', body: 'Prompt library exported, owners assigned, and agreement on what the team standardises on next week.' }
    ]
  },

  deliverables: {
    heading: 'What your team leaves with',
    subheading: 'Artefacts, not attendance.',
    items: [
      { title: 'A working team use case', body: 'Built on real work during the session and demonstrated before anyone leaves.' },
      { title: 'A reusable prompt library', body: 'Documented and exported, so it transfers to people who were not in the room.' },
      { title: 'A house-style drafting pattern', body: 'So documents from different people read like they came from the same organisation.' },
      { title: 'A verification habit for Sheets', body: 'A checking method your team applies before trusting any generated analysis.' },
      { title: 'A named owner per workflow', body: 'Adoption dies when nothing is owned. Each workflow leaves with a person against it.' },
      { title: 'A short adoption plan', body: 'What the team standardises on in week one, and what is deliberately parked.' }
    ]
  },

  qualification: {
    heading: 'Who this training is for.',
    subheading: 'A poor fit wastes a full day on both sides, so we would rather establish that on the call than on the day.',
    isFor: [
      'Teams already licensed for Gemini in Google Workspace',
      'Six or more people who can be released for a full day',
      'Willingness to work in your own Workspace on real files',
      'A leader who can decide what the team standardises on afterwards'
    ],
    isNotFor: [
      'Not yet licensed for Gemini — talk to us about readiness first instead',
      'Looking for an awareness webinar rather than hands-on work',
      'Primarily a Microsoft 365 shop; our Copilot training will fit better',
      'Unable to release the team for an uninterrupted day'
    ]
  },

  guarantee: {
    heading: 'What your team will have built by 5pm.',
    body: 'The session is built around one commitment: your team applies Gemini to real work inside your own Workspace and leaves with at least one working use case they built themselves. We scope the day with you beforehand so that outcome is realistic for your team.'
  },

  trainer: {
    heading: 'Not taught by an AI generalist',
    intro: 'Your team will know within twenty minutes whether the trainer has done real work. This is who runs the session.'
  },

  confidentiality: 'We deliver these sessions under enterprise NDA, and client names are withheld — the same confidentiality that will cover your own session. If you would like a reference conversation, we will arrange one directly with a past client rather than publish a quote.',

  faqs: [
    {
      question: 'Is our data safe during onsite training?',
      answer: 'We sign enterprise NDAs prior to the session, and all hands-on exercises run inside your own Google Workspace under your existing sharing and access controls. We do not move files out of your environment. Google publishes its enterprise data-handling commitments for Gemini in Workspace, and we work within whatever configuration your administrators have set.'
    },
    {
      question: 'What exactly will our team have at the end of the day?',
      answer: 'By the end of the workshop your team will have applied Gemini to real work in your own Workspace and built at least one working use case during the session. We agree the specific use case with you before the session so it is realistic for your team and their data.'
    },
    {
      question: 'How is this different from Google\'s own enablement session?',
      answer: 'A vendor enablement session covers the product surface. We spend the day on your team\'s actual work — your documents, your sheets, your Drive — and leave a shared standard behind rather than individual familiarity.'
    },
    {
      question: 'Do we need technical people in the room?',
      answer: 'No. This is training for the people who do the work. Having a Workspace administrator available for the first hour helps if permissions or Gemini settings need checking, but they do not need to stay.'
    },
    {
      question: 'How many people can attend?',
      answer: 'Up to around 15 works well for hands-on coaching. Beyond that we split into two sessions rather than dilute the room. Pricing is a flat day rate, not per seat.'
    },
    {
      question: 'Do you deliver outside Singapore?',
      answer: 'Yes. We deliver onsite across Singapore and the wider Southeast Asia region, and travel further for larger engagements.'
    }
  ],

  finalCta: {
    eyebrow: 'By application',
    heading: 'You have already paid for the licences.',
    subheading: 'Now invest one day in teaching your team to use them.',
    note: 'One flat day rate for your whole team — no per-seat pricing. Weigh it against the underused licence spend in the calculator above.',
    ps: 'All hands-on exercises run privately inside your organisation\'s Google Workspace, under your existing access controls and an enterprise NDA.'
  },

  leadForm: {
    title: 'Request Onsite Gemini Training',
    subtitle: 'We come to your office. Private session, your own Workspace.',
    buttonLabel: 'Request Onsite Training',
    messageLabel: 'Where does your team lose the most time? (optional)',
    messagePlaceholder: 'e.g. Nobody can find the current version in Drive, decks are rebuilt from scratch every week...',
    successText: 'Thanks — we\'ll respond within 24 hours to scope a private onsite session for your team.'
  }
};

/* ===================================================================== */
/* Amazon Q                                                              */
/* ===================================================================== */

const amazonq = {
  page: '/amazon-q-training',
  product: 'Amazon Q',
  roi: { productName: 'Amazon Q', defaultSeatCost: 240 },

  hero: {
    eyebrow: '1-Day Amazon Q Training • Delivered Onsite at Your Office',
    image: '/images/amazon-training.png',
    titleLead: 'Your data is in AWS. Your answers are still ',
    titleAccent: 'three tickets away',
    titleTail: '.',
    sub: 'We train your data, BI and engineering teams onsite, against your own connected sources, to build Amazon Q workflows that answer business questions without a queue — in one day.',
    ctaLabel: 'Book Your 1-Day Amazon Q Training →',
    gtag: 'Hero Book Amazon Q Training',
    security: 'Runs inside your own AWS account, under your existing IAM permissions. Enterprise NDA.',
    badges: [
      { color: '#34d399', title: 'Private 1 Day', sub: 'Intensive' },
      { color: '#38bdf8', title: '70% Hands-On', sub: 'Active Learning' },
      { color: '#c084fc', title: 'Data Team Built', sub: 'BI & Analytics Workflows' },
      { color: '#fbbf24', title: 'Your Sources', sub: 'Your Connectors, Your IAM' }
    ]
  },

  comparison: {
    heading: 'This is not an Amazon Q demo. It is a working session against your own sources.',
    lead: 'The demo always works. What breaks is your permission model, your inconsistent schema and the fact that three teams define revenue differently.',
    genericTitle: 'Generic AI Training',
    generic: [
      { strong: 'Feature tour', text: 'Covers what the product can do rather than what your connected sources will actually let it do.' },
      { strong: 'Clean demo data', text: 'Uses tidy sample datasets that hide every problem you will hit in week one.' },
      { strong: 'Ignores permissions', text: 'Skips the IAM and connector reality that determines who can ask what.' },
      { strong: 'No verification', text: 'Never teaches how to check a generated answer before someone puts it in a board pack.' }
    ],
    specialisedTitle: 'Specialised Amazon Q Training',
    specialised: [
      { strong: 'Your sources', text: 'Work against your own connected data, with your own permission boundaries in place.' },
      { strong: 'Practitioner led', text: 'Taught by someone who has built data platforms in regulated environments, including access and governance design.' },
      { strong: 'Verification first', text: 'Every workflow includes how the answer gets checked before anyone relies on it.' },
      { strong: 'Beyond prompting', text: 'A reusable pattern library and at least one working use case built during the session.' }
    ]
  },

  learnDo: {
    heading: 'Six analytics workflows, built by your team, during the day.',
    subheading: 'Roughly 30% instruction, 70% hands-on, against your own connected sources.',
    pills: ['Business Q&A', 'Data Discovery', 'Dashboard Drafting', 'Query Explanation', 'Document Search', 'Answer Verification']
  },

  beforeAfter: {
    heading: 'What changes the next morning',
    subheading: 'Specific bottlenecks, not general capability.',
    rows: [
      { before: 'Every business question becomes a ticket in the data team backlog', after: 'Answered directly by the requester, within their own permission boundary' },
      { before: 'Analysts hunt for which table is the current source of truth', after: 'Discovery grounded in your catalogue, with the source surfaced in the answer' },
      { before: 'Dashboard requests wait weeks for a free analyst', after: 'First-cut dashboards drafted by the requester and refined by the data team' },
      { before: 'Inherited SQL nobody dares to change', after: 'Explained in plain language before anyone edits it' },
      { before: 'Generated numbers trusted because they look plausible', after: 'Checked against a known result using an agreed verification step' }
    ]
  },

  agenda: {
    heading: 'One day. Your office. Hour by hour.',
    subheading: 'Short teaching blocks, then your team works against your own connected sources.',
    items: [
      { time: '9:00', title: 'Foundations and boundaries', body: 'What Amazon Q can answer, what it cannot, and how your IAM permissions and connector configuration decide what each person actually sees.' },
      { time: '10:00', title: 'Business questions without a ticket', body: 'Build the question patterns that work against your data, and learn to recognise the phrasing that produces confidently wrong answers.' },
      { time: '11:30', title: 'Discovery and source of truth', body: 'Find the right dataset rather than the first plausible one. Ground answers so the source is surfaced and can be challenged.' },
      { time: '1:30', title: 'Dashboards and query explanation', body: 'Draft a first-cut dashboard from a business question, and use Q to explain inherited SQL before anyone modifies it.' },
      { time: '3:00', title: 'Verification: the part everyone skips', body: 'Establish how a generated answer gets checked against a known result before it reaches a decision-maker. This is the difference between adoption and a retraction email.' },
      { time: '4:00', title: 'Beyond prompting: build a working use case', body: 'Each group builds one workflow end to end against real sources and demonstrates it to the room.' },
      { time: '5:00', title: 'Take it back to work', body: 'Pattern library exported, owners assigned, and agreement on which questions move out of the backlog next week.' }
    ]
  },

  deliverables: {
    heading: 'What your team leaves with',
    subheading: 'Working artefacts against your own sources.',
    items: [
      { title: 'A working use case', body: 'Built against your own connected data during the session and demonstrated before anyone leaves.' },
      { title: 'A reusable pattern library', body: 'The question and prompt patterns that worked against your schema, documented and exported.' },
      { title: 'A verification standard', body: 'An agreed method for checking a generated answer before it informs a decision.' },
      { title: 'A permission-boundary map', body: 'A clear picture of who can ask what, so nobody is surprised by an empty answer or an over-broad one.' },
      { title: 'A named owner per workflow', body: 'Each workflow leaves the room with a person accountable for it.' },
      { title: 'A short adoption plan', body: 'Which questions move out of the data backlog first, and what stays with the data team.' }
    ]
  },

  qualification: {
    heading: 'Who this training is for.',
    subheading: 'This one has a hard prerequisite: Amazon Q needs to be connected to something before training is worth your day.',
    isFor: [
      'Teams with Amazon Q already provisioned and at least one data source connected',
      'Six or more analysts, BI staff or business users who can be released for a day',
      'Willingness to work against your own connected sources',
      'A data or analytics leader who can decide what gets adopted'
    ],
    isNotFor: [
      'Amazon Q not yet provisioned or connected — we would be training on nothing',
      'Looking for an AWS architecture workshop rather than user enablement',
      'Expecting us to build connectors or configure IAM on the day',
      'Unable to release the team for an uninterrupted day'
    ]
  },

  guarantee: {
    heading: 'What your team will have built by 5pm.',
    body: 'The session is built around one commitment: your team applies Amazon Q to real questions against your own connected sources and leaves with at least one working use case they built themselves. We scope the day with you beforehand so that outcome is realistic for your team.'
  },

  trainer: {
    heading: 'Not taught by an AI generalist',
    intro: 'Data teams spot a demo specialist immediately. This is who runs the session and what they have built.'
  },

  confidentiality: 'We deliver these sessions under enterprise NDA, and client names are withheld — the same confidentiality that will cover your own session. If you would like a reference conversation, we will arrange one directly with a past client rather than publish a quote.',

  faqs: [
    {
      question: 'Is our data safe during onsite training?',
      answer: 'We sign enterprise NDAs prior to the session, and all hands-on exercises run inside your own AWS account against your existing connectors and IAM permissions. We do not extract data or move it out of your environment, and participants only ever see what their own permissions already allow.'
    },
    {
      question: 'What has to be in place before the session?',
      answer: 'Amazon Q provisioned, at least one meaningful data source connected, and participants with working access. If that is not in place yet, the training day would be spent watching setup — so we would rather help you scope readiness first and book the session afterwards.'
    },
    {
      question: 'Will you configure connectors or IAM for us on the day?',
      answer: 'No. That is implementation work with its own security review, and doing it live in a training room is a bad idea. We can scope it separately. The training day assumes a working connection and focuses on getting real value out of it.'
    },
    {
      question: 'What exactly will our team have at the end of the day?',
      answer: 'By the end of the workshop your team will have applied Amazon Q to real questions against your own connected sources and built at least one working use case during the session. We agree the specific use case with you before the session so it is realistic for your team and their data.'
    },
    {
      question: 'Is this for engineers or business users?',
      answer: 'Both, and the mix works well. Business users learn what they can now answer themselves; the data team learns where the boundaries and verification steps need to sit. Sessions with only one group tend to be less useful than sessions with both.'
    },
    {
      question: 'Do you deliver outside Singapore?',
      answer: 'Yes. We deliver onsite across Singapore and the wider Southeast Asia region, and travel further for larger engagements.'
    }
  ],

  finalCta: {
    eyebrow: 'By application',
    heading: 'You have already paid for the licences.',
    subheading: 'Now invest one day in getting answers out of them.',
    note: 'One flat day rate for your whole team — no per-seat pricing. Weigh it against the underused licence spend in the calculator above.',
    ps: 'All hands-on exercises run inside your own AWS account, under your existing IAM permissions and an enterprise NDA.'
  },

  leadForm: {
    title: 'Request Onsite Amazon Q Training',
    subtitle: 'We come to your office. Private session, your own connected sources.',
    buttonLabel: 'Request Onsite Training',
    messageLabel: 'What is sitting in your data backlog? (optional)',
    messagePlaceholder: 'e.g. Every business question becomes a ticket, dashboard requests wait three weeks...',
    successText: 'Thanks — we\'ll respond within 24 hours to scope a private onsite session for your team.'
  }
};

/* ===================================================================== */
/* Claude for enterprise teams                                           */
/* ===================================================================== */

const claude = {
  page: '/claude-cowork-training',
  product: 'Claude',
  roi: { productName: 'Claude', defaultSeatCost: 360 },

  hero: {
    eyebrow: '1-Day Claude Training • Delivered Onsite at Your Office',
    image: '/images/claude-training.png',
    titleLead: 'Your team uses Claude like a search box. It is ',
    titleAccent: 'a reasoning tool',
    titleTail: '.',
    sub: 'We train your knowledge workers onsite, on your own documents and contracts, to build Claude workflows for long-document analysis, synthesis and drafting that hold up under review — in one day.',
    ctaLabel: 'Book Your 1-Day Claude Training →',
    gtag: 'Hero Book Claude Training',
    security: 'Runs under your organisation\'s approved Claude deployment and data controls. Enterprise NDA.',
    badges: [
      { color: '#34d399', title: 'Private 1 Day', sub: 'Intensive' },
      { color: '#38bdf8', title: '70% Hands-On', sub: 'Active Learning' },
      { color: '#c084fc', title: 'Knowledge Work', sub: 'Document & Analysis Workflows' },
      { color: '#fbbf24', title: 'Your Documents', sub: 'Contracts, Reports, Research' }
    ]
  },

  comparison: {
    heading: 'This is not a Claude demo. It is a working session on your own documents.',
    lead: 'Most teams use a long-context reasoning model to summarise a two-page email. The value is in the 200-page contract, the six conflicting reports, and the analysis nobody has time to do properly.',
    genericTitle: 'Generic AI Training',
    generic: [
      { strong: 'Prompt tricks', text: 'A list of clever prompts, with nothing that survives a real document set.' },
      { strong: 'Short-context habits', text: 'Teaches question-and-answer patterns that waste what a long-context model is actually good at.' },
      { strong: 'Sample documents', text: 'Demo PDFs that are cleaner and shorter than anything in your own repository.' },
      { strong: 'No review discipline', text: 'Never addresses how a synthesis gets checked before it reaches a client or a regulator.' }
    ],
    specialisedTitle: 'Specialised Claude Training',
    specialised: [
      { strong: 'Long-document work', text: 'Contract review, multi-report synthesis, structured extraction and comparison across large document sets.' },
      { strong: 'Practitioner led', text: 'Taught by someone who has delivered AI systems into regulated environments where output gets audited.' },
      { strong: 'Your documents', text: 'Your own contracts, reports and research — redacted where confidentiality requires it.' },
      { strong: 'Review built in', text: 'Every workflow includes how the output gets verified against source before anyone relies on it.' }
    ]
  },

  learnDo: {
    heading: 'Six knowledge workflows, built by your team, during the day.',
    subheading: 'Roughly 30% instruction, 70% hands-on, on your own document set.',
    pills: ['Long-Document Analysis', 'Contract Review', 'Multi-Source Synthesis', 'Structured Extraction', 'Drafting & Redrafting', 'Source Verification']
  },

  beforeAfter: {
    heading: 'What changes the next morning',
    subheading: 'Specific work, not general capability.',
    rows: [
      { before: 'A 200-page agreement read cover to cover before anyone can advise on it', after: 'Key clauses, deviations and risks extracted with the page cited, then read in full where it matters' },
      { before: 'Six reports summarised separately and reconciled by hand', after: 'Synthesised across sources with the disagreements surfaced rather than smoothed over' },
      { before: 'Structured data pulled out of documents by copy-paste', after: 'Extracted into a consistent schema, spot-checked against source' },
      { before: 'First drafts written from scratch every time', after: 'Drafted against your own house patterns and precedent, then edited by the expert' },
      { before: 'Output trusted because it reads well', after: 'Checked against the source document as a standing step in the workflow' }
    ]
  },

  agenda: {
    heading: 'One day. Your office. Hour by hour.',
    subheading: 'Short teaching blocks, then your team works on your own documents.',
    items: [
      { time: '9:00', title: 'Foundations for knowledge work', body: 'What a long-context reasoning model does well, where it fails, and why the habits people bring from short chat tools waste most of its value.' },
      { time: '10:00', title: 'Long-document analysis', body: 'Work a real long document end to end. Extract, locate, cite and challenge — including how to make the model show you where an answer came from.' },
      { time: '11:30', title: 'Contract and agreement review', body: 'Clause extraction, deviation against your standard positions, and risk flagging — with the legal judgement left firmly with your people.' },
      { time: '1:30', title: 'Multi-source synthesis', body: 'Combine several documents that partially disagree, and get the conflicts surfaced rather than averaged away. This is where most teams get the biggest gain.' },
      { time: '3:00', title: 'Drafting and structured extraction', body: 'Draft against your own precedent and house style, and extract structured data into a consistent schema you can actually use downstream.' },
      { time: '4:00', title: 'Beyond prompting: build a working use case', body: 'Each group builds one workflow end to end on real documents and demonstrates it to the room.' },
      { time: '5:00', title: 'Take it back to work', body: 'Prompt library exported, owners assigned, and agreement on the review step that applies before any output is relied upon.' }
    ]
  },

  deliverables: {
    heading: 'What your team leaves with',
    subheading: 'Artefacts your team can apply to the next matter.',
    items: [
      { title: 'A working knowledge use case', body: 'Built on your own documents during the session and demonstrated before anyone leaves.' },
      { title: 'A reusable prompt library', body: 'Documented and exported, so the patterns transfer to people who were not in the room.' },
      { title: 'A long-document review pattern', body: 'Extraction and citation habits that make output checkable against the source.' },
      { title: 'A synthesis pattern for conflicting sources', body: 'A method that surfaces disagreement between documents rather than hiding it.' },
      { title: 'A verification step', body: 'An agreed review discipline applied before any output reaches a client, board or regulator.' },
      { title: 'A short adoption plan', body: 'What the team uses on the next matter, and what is deliberately kept manual.' }
    ]
  },

  qualification: {
    heading: 'Who this training is for.',
    subheading: 'This training earns its keep where documents are long and the cost of a wrong answer is real.',
    isFor: [
      'Teams with an approved organisational Claude deployment in place',
      'Six or more knowledge workers who can be released for a full day',
      'Work involving long documents, contracts, research or multi-source analysis',
      'A leader who can decide what the team adopts and what review step applies'
    ],
    isNotFor: [
      'No approved deployment yet — talk to us about readiness and data controls first',
      'Looking for a prompt-engineering webinar rather than hands-on work',
      'Work that is mostly short-form email and chat; the gain will be small',
      'Unable to release the team for an uninterrupted day'
    ]
  },

  guarantee: {
    heading: 'What your team will have built by 5pm.',
    body: 'The session is built around one commitment: your team applies Claude to real documents and leaves with at least one working use case they built themselves. We scope the day with you beforehand so that outcome is realistic for your team.'
  },

  trainer: {
    heading: 'Not taught by an AI generalist',
    intro: 'Knowledge teams are hard to impress and quick to spot a demo. This is who runs the session.'
  },

  confidentiality: 'We deliver these sessions under enterprise NDA, and client names are withheld — the same confidentiality that will cover your own session. If you would like a reference conversation, we will arrange one directly with a past client rather than publish a quote.',

  faqs: [
    {
      question: 'Is our confidential material safe during onsite training?',
      answer: 'We sign enterprise NDAs prior to the session, and all hands-on exercises use your organisation\'s own approved Claude deployment under whatever data controls your administrators have configured. Where material is especially sensitive we work from redacted versions your team prepares. We do not retain copies of anything used on the day.'
    },
    {
      question: 'Can Claude review our contracts for us?',
      answer: 'It can extract clauses, flag deviations from your standard positions and surface risks far faster than reading cover to cover. The legal judgement stays with your lawyers. We build every review workflow so the output is cited back to the source document and checked, rather than accepted because it reads convincingly.'
    },
    {
      question: 'What exactly will our team have at the end of the day?',
      answer: 'By the end of the workshop your team will have applied Claude to real documents and built at least one working use case during the session. We agree the specific use case with you before the session so it is realistic for your team and their data.'
    },
    {
      question: 'We already use ChatGPT or Copilot. Is this redundant?',
      answer: 'Not usually. Most organisations end up with more than one tool, and the useful skill is knowing which work belongs where. This session focuses on long-document reasoning and synthesis, which is the work teams most often do badly with a general chat habit.'
    },
    {
      question: 'How many people can attend?',
      answer: 'Up to around 15 works well for hands-on coaching. Beyond that we split into two sessions rather than dilute the room. Pricing is a flat day rate, not per seat.'
    },
    {
      question: 'Do you deliver outside Singapore?',
      answer: 'Yes. We deliver onsite across Singapore and the wider Southeast Asia region, and travel further for larger engagements.'
    }
  ],

  finalCta: {
    eyebrow: 'By application',
    heading: 'You have already paid for the licences.',
    subheading: 'Now invest one day in teaching your team to use them properly.',
    note: 'One flat day rate for your whole team — no per-seat pricing. Weigh it against the underused licence spend in the calculator above.',
    ps: 'All hands-on exercises run under your organisation\'s approved Claude deployment and data controls, and an enterprise NDA.'
  },

  leadForm: {
    title: 'Request Onsite Claude Training',
    subtitle: 'We come to your office. Private session, your own documents.',
    buttonLabel: 'Request Onsite Training',
    messageLabel: 'What kind of documents eat your team\'s time? (optional)',
    messagePlaceholder: 'e.g. 200-page supplier agreements, quarterly research reports that need reconciling...',
    successText: 'Thanks — we\'ll respond within 24 hours to scope a private onsite session for your team.'
  }
};


/* ===================================================================== */
/* ChatGPT Enterprise                                                    */
/* ===================================================================== */

const chatgpt = {
  page: '/chatgpt-enterprise-training',
  product: 'ChatGPT Enterprise',
  roi: { productName: 'ChatGPT', defaultSeatCost: 720 },

  hero: {
    eyebrow: '1-Day ChatGPT Enterprise Training • Delivered Onsite at Your Office',
    image: '/images/onsite-training.png',
    // video: '/videos/chatgpt-bg.mp4',  // defocused stock B-roll — subjects unreadable.
    // Uncomment to re-enable; TrainingPage supports hero.video on any page.
    titleLead: 'Everyone has ChatGPT. Almost nobody has ',
    titleAccent: 'built anything with it',
    titleTail: '.',
    sub: 'We train your team onsite, on your own files and data, to move past asking questions — building shared custom GPTs, analysing real spreadsheets, and standardising the work so it survives the person who made it.',
    ctaLabel: 'Book Your 1-Day ChatGPT Training →',
    gtag: 'Hero Book ChatGPT Training',
    security: 'Runs inside your organisation\u2019s ChatGPT Enterprise workspace, under your existing admin controls. Enterprise NDA.',
    badges: [
      { color: '#34d399', title: 'Private 1 Day', sub: 'Intensive' },
      { color: '#38bdf8', title: '70% Hands-On', sub: 'Active Learning' },
      { color: '#c084fc', title: 'Custom GPTs', sub: 'Built By Your Team' },
      { color: '#fbbf24', title: 'Your Data', sub: 'Your Files, Your Workspace' }
    ]
  },

  comparison: {
    heading: 'This is not a prompting webinar. Your team builds things that other people then use.',
    lead: 'Individual familiarity with ChatGPT is already universal and worth very little. The gap is between forty people each improvising in their own chat window and a team with shared, reviewed assistants that produce the same quality whoever runs them.',
    genericTitle: 'Generic AI Training',
    generic: [
      { strong: 'Prompt lists', text: 'A deck of clever prompts that nobody opens again after the session ends.' },
      { strong: 'Chat-only habits', text: 'Never reaches custom GPTs, file analysis or the connectors where the real leverage sits.' },
      { strong: 'Sample data', text: 'Tidy demo files that behave nothing like your actual exports and documents.' },
      { strong: 'No standard', text: 'Everyone leaves with a personal trick and the organisation learns nothing.' }
    ],
    specialisedTitle: 'Specialised Enterprise Training',
    specialised: [
      { strong: 'Shared custom GPTs', text: 'Built during the session on your own material, then published to the people who need them.' },
      { strong: 'Practitioner led', text: 'Taught by someone who has delivered enterprise systems in regulated environments, not a prompt influencer.' },
      { strong: 'Your workspace', text: 'Your own ChatGPT Enterprise tenant, your files, your admin and retention settings.' },
      { strong: 'Verification built in', text: 'Every workflow includes how output gets checked before anyone relies on it.' }
    ]
  },

  learnDo: {
    heading: 'Six workflows, built by your team, during the day.',
    subheading: 'Roughly 30% instruction, 70% hands-on. Everyone works in your own workspace, on work they actually own.',
    pills: ['Custom GPTs', 'Spreadsheet Analysis', 'Long Document Review', 'Research & Synthesis', 'Drafting Standards', 'Output Verification']
  },

  beforeAfter: {
    heading: 'What changes the next morning',
    subheading: 'Specific tasks, not general capability.',
    rows: [
      { before: 'Forty people improvising forty different prompts for the same task', after: 'One shared custom GPT everyone opens, producing consistent output' },
      { before: 'Spreadsheet analysis waiting on the one person who knows the formulas', after: 'Described in plain language, generated, then checked against a known result' },
      { before: 'Long reports and contracts read end to end before anyone can act', after: 'Key points extracted with the source cited, then read closely where it matters' },
      { before: 'Research that means twenty browser tabs and a lost afternoon', after: 'Structured synthesis with sources listed, ready to verify' },
      { before: 'Output trusted because it reads well', after: 'Checked against source as a standing step before it goes anywhere' }
    ]
  },

  agenda: {
    heading: 'One day. Your office. Hour by hour.',
    subheading: 'Short teaching blocks, then your team builds in your own workspace.',
    items: [
      { time: '9:00', title: 'What it does well, and where it lies', body: 'The failure modes that matter in an enterprise: confident wrong answers, stale knowledge, and the tasks where it should not be used at all. Short and practical.' },
      { time: '10:00', title: 'Working with your own files', body: 'Upload real documents and exports. Extract, compare and summarise with the source cited, so a reviewer can check rather than trust.' },
      { time: '11:30', title: 'Spreadsheet and data analysis', body: 'Describe the analysis in plain language, generate it, and — the part most training skips — verify it against a result you already know.' },
      { time: '1:30', title: 'Build a custom GPT', body: 'The core of the day. Each group builds a GPT for a task their team repeats weekly, with its own instructions and reference files, then tests it against real cases.' },
      { time: '3:00', title: 'Publish, review and govern', body: 'Who can see it, how it gets reviewed before sharing, and how you stop forty near-identical GPTs appearing by Christmas.' },
      { time: '4:00', title: 'Demonstrate to the room', body: 'Each group shows what they built and hands it to someone who was not involved, to see whether it survives contact with a colleague.' },
      { time: '5:00', title: 'Take it back to work', body: 'GPTs published, prompt library exported, owners assigned, and agreement on what the team standardises on next week.' }
    ]
  },

  deliverables: {
    heading: 'What your team leaves with',
    subheading: 'Artefacts, not attendance.',
    items: [
      { title: 'At least one published custom GPT', body: 'Built on your own material during the session, tested by someone who did not build it, and available to the team on Monday.' },
      { title: 'A reusable prompt library', body: 'Documented and exported, so it transfers to people who were not in the room.' },
      { title: 'A verification habit', body: 'An agreed way of checking generated analysis and claims before they inform a decision.' },
      { title: 'A house drafting standard', body: 'So documents from different people read like they came from one organisation.' },
      { title: 'A governance line', body: 'Who reviews a GPT before it is shared, and what is not allowed near the tool at all.' },
      { title: 'A short adoption plan', body: 'What the team standardises on in week one, and what is deliberately parked.' }
    ]
  },

  qualification: {
    heading: 'Who this training is for.',
    subheading: 'A poor fit wastes a full day on both sides, so we would rather establish that on the call than on the day.',
    isFor: [
      'Teams already licensed for ChatGPT Enterprise, Team or Edu',
      'Six or more people who can be released for a full day',
      'Willingness to work on your own documents and data',
      'A leader who can decide what the team standardises on afterwards'
    ],
    isNotFor: [
      'Not yet licensed — free ChatGPT accounts have different data handling and we will not train on them',
      'Looking for an awareness webinar rather than hands-on work',
      'Expecting us to integrate ChatGPT with your internal systems; this is training, not implementation',
      'Unable to release the team for an uninterrupted day'
    ]
  },

  guarantee: {
    heading: 'What your team will have built by 5pm.',
    body: 'The session is built around one commitment: your team publishes at least one working custom GPT, built on your own material and tested by a colleague who did not build it. We scope the day with you beforehand so that outcome is realistic for your team.'
  },

  trainer: {
    heading: 'Not taught by an AI generalist',
    intro: 'Your team will know within twenty minutes whether the trainer has done real work. This is who runs the session.'
  },

  confidentiality: 'We deliver these sessions under enterprise NDA, and client names are withheld — the same confidentiality that will cover your own session. If you would like a reference conversation, we will arrange one directly with a past client rather than publish a quote.',

  faqs: [
    {
      question: 'Is our data safe during onsite training?',
      answer: 'We sign enterprise NDAs prior to the session, and all hands-on exercises run inside your own ChatGPT Enterprise workspace under the admin, retention and access settings your administrators have configured. OpenAI states that business data submitted through its enterprise offerings is not used to train its models by default. We work within whatever configuration you already have rather than changing it.'
    },
    {
      question: 'What exactly will our team have at the end of the day?',
      answer: 'At least one published custom GPT built on your own material and tested by a colleague who did not build it, plus an exported prompt library and an agreed verification step. We agree the specific use case with you before the session so it is realistic for your team and their data.'
    },
    {
      question: 'We already use Copilot. Is this redundant?',
      answer: 'Not usually. Most organisations end up with more than one tool, and the useful skill is knowing which work belongs where. Copilot sits inside Office and your tenant data; ChatGPT is stronger for open-ended analysis, research and building shared custom assistants. We will say plainly which of your tasks belong in which.'
    },
    {
      question: 'Do we need technical people in the room?',
      answer: 'No. Custom GPTs are built through configuration and instructions, not code. Having a workspace administrator available for the first hour helps if permissions or sharing settings need checking, but they do not need to stay.'
    },
    {
      question: 'How do we stop ending up with fifty near-identical GPTs?',
      answer: 'That is a real failure mode and we spend a block of the afternoon on it: who reviews a GPT before it is shared, how they are named and owned, and when to improve an existing one rather than build another. Governance is part of the training, not a follow-up conversation.'
    },
    {
      question: 'Do you deliver outside Singapore?',
      answer: 'Yes. We deliver onsite across Singapore and the wider Southeast Asia region, and travel further for larger engagements. Remote delivery is possible but hands-on coaching is noticeably better in the room.'
    }
  ],

  finalCta: {
    eyebrow: 'By application',
    heading: 'You have already paid for the licences.',
    subheading: 'Now invest one day in teaching your team to build with them.',
    note: 'One flat day rate for your whole team — no per-seat pricing. Weigh it against the underused licence spend in the calculator above.',
    ps: 'All hands-on exercises run privately inside your organisation\u2019s own ChatGPT Enterprise workspace, under your existing admin controls and an enterprise NDA.'
  },

  leadForm: {
    title: 'Request Onsite ChatGPT Training',
    subtitle: 'We come to your office. Private session, your own files and workspace.',
    buttonLabel: 'Request Onsite Training',
    messageLabel: 'What does your team repeat every week? (optional)',
    messagePlaceholder: 'e.g. Everyone writes the same client update from scratch, spreadsheet analysis queues behind one person...',
    successText: 'Thanks — we\u2019ll respond within 24 hours to scope a private onsite session for your team.'
  }
};

const trainingContent = { hr, manufacturing, gemini, amazonq, claude, chatgpt };

export default trainingContent;
